// 证书管理相关的API服务
import api from '@/api/api.js'

// 证书相关API接口
const certificateAPI = {
  // 获取证书列表分页查询
  // 请求参数: {certificateCode: string, startTime: string, endTime: string, pageSize: number, pageNo: string}
  // 返回格式: {code: number, msg: string, data: {pageSize: string, pageNo: string, count: string, data: [...]}}
  getCertificateList: (params) => {
    return api.post('/certificate/page', params)
  },
  
  // 获取单个证书详情
  getCertificateDetail: (id) => {
    return api.get(`/certificate/${id}`)
  },
  
  // 通过id查询证书信息（按照接口文档要求）
  getCertificateById: (id) => {
    return api.get('/certificate/get-by-id', { params: { id } })
  },
  
  // 修改证书信息
  // 请求参数: {certificateCode: string, certificateCompanyName: string, instrumentName: string, modelSpecification: number, factorySerialCode: string}
  // 返回格式: {code: number, msg: string, data: object}
  updateCertificate: (data) => {
    return api.post('/certificate/update', data)
  },
  
  // 删除证书
  // 请求参数: {ids: [number]}
  // 返回格式: {code: number, msg: string, data: object}
  deleteCertificate: (id) => {
    return api.post('/certificate/delete', { ids: [id] })
  },
  
  // 批量删除证书
  // 请求参数: {ids: [number]}
  // 返回格式: {code: number, msg: string, data: object}
  batchDeleteCertificates: (ids) => {
    return api.post('/certificate/delete', { ids })
  },
  
  // 下载单个二维码
  downloadQrCode: async (id) => {
    // 参数验证
    if (!id || id === undefined || id === null) {
      throw new Error('缺少必需参数: id')
    }
    
    const response = await api.get('/certificate/download-qrcode', {
      responseType: 'blob',
      params: { id }
    })
    
    // 获取响应头中的content-type
    const contentType = response.headers.get('content-type') || ''
    
    // 检查是否包含JSON格式的错误响应
    if (contentType.includes('application/json') && response.data) {
      try {
        // 尝试将Blob转换为JSON字符串
        const jsonString = await response.data.text()
        const jsonData = JSON.parse(jsonString)
        
        // 如果解析成功且包含code字段
        if (jsonData && typeof jsonData.code === 'number') {
          if (jsonData.code !== 0) {
            // 业务码不为0，抛出错误
            throw new Error(jsonData.msg || '二维码下载失败')
          }
        }
      } catch (error) {
        // JSON解析失败，可能是正常的文件数据
        console.debug('不是JSON格式的响应，继续处理文件数据')
      }
    }
    
    // 检查是否是有效的文件数据
    if (!response.data || response.data.size === 0) {
      throw new Error('下载的二维码文件为空')
    }
    
    // 直接返回Blob对象
    return response.data
  },
  
  // 批量下载二维码
  batchDownloadQrCodes: async (params) => {
    // 参数验证
    if (!params || !params.ids || !Array.isArray(params.ids) || params.ids.length === 0) {
      throw new Error('缺少必需参数: ids')
    }
    
    const response = await api.post('/certificate/batch-download-qrcode', params, { responseType: 'blob' })
    
    // 获取响应头中的content-type
    const contentType = response.headers.get('content-type') || ''
    
    // 检查是否包含JSON格式的错误响应
    if (contentType.includes('application/json') && response.data) {
      try {
        // 尝试将Blob转换为JSON字符串
        const jsonString = await response.data.text()
        const jsonData = JSON.parse(jsonString)
        
        // 如果解析成功且包含code字段
        if (jsonData && typeof jsonData.code === 'number') {
          if (jsonData.code !== 0) {
            // 业务码不为0，抛出错误
            throw new Error(jsonData.msg || '二维码批量下载失败')
          }
        }
      } catch (error) {
        // JSON解析失败，可能是正常的文件数据
        console.debug('不是JSON格式的响应，继续处理文件数据')
      }
    }
    
    // 检查是否是有效的文件数据
    if (!response.data || response.data.size === 0) {
      throw new Error('下载的ZIP文件为空')
    }
    
    // 直接返回Blob对象
    return response.data
  },
  
  // 导入证书信息
  importCertificates: (file) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post('/certificate/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  },
  
  // 根据证书编号查询证书（未删除的数据）
  queryCertificate: (certificateNo) => {
    return api.get('/certificate/query-by-code', { params: { certificateCode: certificateNo } })
  },
  
  // 导出Excel - 在API层解析响应并判断类型
  exportExcel: async (params) => {
    const response = await api.post('/certificate/export-excel', params, { responseType: 'arraybuffer' })
    
    // 获取响应头中的content-type
    const contentType = response.headers.get('content-type') || ''
    
    // 检查是否包含JSON格式的错误响应
    if (contentType.includes('application/json')) {
      // 尝试解析为JSON
      const jsonString = new TextDecoder('utf-8').decode(response.data)
      const jsonData = JSON.parse(jsonString)
      
      // 如果解析成功且包含code字段
      if (jsonData && typeof jsonData.code === 'number') {
        if (jsonData.code !== 0) {
          // 业务码不为0，抛出错误
          throw new Error(jsonData.msg || '导出失败')
        }
      }
    }
    
    // 检查是否是有效的文件数据
    if (!response.data || response.data.byteLength === 0) {
      throw new Error('导出的文件为空')
    }
    
    // 转换为Blob并返回
    return new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  }
}

// 导出API
export default certificateAPI